import ServiceTemplate from '../../../templates/service/ServiceTemplate';
import type { ServiceConfig } from '../../../templates/service/types';

const config: ServiceConfig = {
  pageTitle: 'Electrical Safety & Compliance Sleaford | Quantum Electrical Services',
  metaDescription: 'EICR reports, PAT testing, landlord electrical safety and compliance services in Sleaford, Lincolnshire. NAPIT registered electricians. Call 01529 265060.',
  keywords: 'EICR Sleaford, electrical safety certificate Sleaford, PAT testing Sleaford, landlord electrical certificate Sleaford Lincolnshire, electrical inspection Sleaford, electrical compliance Sleaford',
  canonicalPath: '/services/safety-compliance',

  heroBadge: 'NAPIT Registered · Sleaford & Lincolnshire',
  heroTitle: 'Electrical Safety &',
  heroHighlight: 'Compliance',
  heroLocation: 'Sleaford, Lincolnshire',
  heroDesc: 'Keep your Sleaford property safe, legal and compliant. Quantum Electrical provides EICR reports, PAT testing, fire alarm inspections and all the electrical safety certificates your home, rental property or business needs — delivered by NAPIT registered electricians with detailed, professional documentation.',
  heroImageUrl: 'https://public.readdy.ai/ai/img_res/8d0dd55077acfe86b66ed2c48cdf0a0a.jpg',

  servicesHeading: 'Every Safety & Compliance Service We Offer',
  servicesSubheading: 'Comprehensive electrical safety, inspection and compliance services for homeowners, landlords and businesses across Sleaford and Lincolnshire.',
  services: [
    { icon: 'ri-file-certificate-fill', title: 'EICR Reports', desc: 'Electrical Installation Condition Reports for landlords, homeowners and businesses in Sleaford — fully compliant with BS7671.' },
    { icon: 'ri-bar-chart-fill', title: 'PAT Testing', desc: 'Portable appliance testing for offices, rental properties and commercial premises throughout Lincolnshire.' },
    { icon: 'ri-alarm-warning-fill', title: 'Fire Alarm Testing & Inspection', desc: 'BS 5839 fire alarm system testing, inspections and certification for commercial and domestic properties in Sleaford.' },
    { icon: 'ri-flashlight-fill', title: 'Emergency Lighting Testing', desc: 'Emergency escape lighting inspections and certification for commercial buildings and HMOs across Lincolnshire.' },
    { icon: 'ri-home-fill', title: 'Landlord Electrical Safety', desc: 'Full landlord electrical compliance service including EICR and required remedial work in Sleaford rental properties.' },
    { icon: 'ri-search-eye-fill', title: 'Pre-Purchase Electrical Survey', desc: 'Thorough electrical survey before buying a Sleaford property — know exactly what you\'re taking on.' },
    { icon: 'ri-shield-check-fill', title: 'Smoke & CO Alarm Inspection', desc: 'Smoke and carbon monoxide alarm testing, compliance checks and new installations across Sleaford.' },
    { icon: 'ri-building-fill', title: 'HMO Electrical Compliance', desc: 'HMO electrical inspections, EICR reports and compliance upgrades for Sleaford landlords and letting agents.' },
    { icon: 'ri-global-fill', title: 'Solar PV System Inspection', desc: 'Electrical safety inspection and performance testing for existing solar PV systems across Lincolnshire.' },
    { icon: 'ri-file-paper-fill', title: 'Electrical Safety Surveys', desc: 'Comprehensive written electrical safety surveys with remedial recommendations for any Sleaford property.' },
    { icon: 'ri-community-fill', title: 'Commercial Compliance', desc: 'Full commercial electrical compliance services including EICR, emergency lighting and fire alarm certification.' },
    { icon: 'ri-tools-fill', title: 'Remedial Works', desc: 'All EICR-identified remedial works carried out and certified by the same NAPIT registered team in Sleaford.' },
  ],

  whyUs: [
    {
      icon: 'ri-file-certificate-fill',
      title: 'Detailed Reports',
      desc: 'Our EICR reports are thorough, clearly written and easy to understand — not just tick-box exercises.',
    },
    {
      icon: 'ri-shield-check-fill',
      title: 'NAPIT Registered',
      desc: 'All electrical safety work in Sleaford is carried out by our NAPIT registered, qualified electricians.',
    },
    {
      icon: 'ri-time-fill',
      title: 'Fast Turnaround',
      desc: 'Most EICR reports in Sleaford are issued within 24–48 hours of inspection — keeping your letting on track.',
    },
    {
      icon: 'ri-tools-fill',
      title: 'Remedials Included',
      desc: 'We carry out all required remedial works ourselves — one team, one invoice, no juggling contractors.',
    },
  ],

  processSteps: [
    {
      number: '01',
      icon: 'ri-phone-fill',
      title: 'Book',
      desc: 'Call or message to book your electrical inspection or safety certificate in Sleaford — quick and easy.',
    },
    {
      number: '02',
      icon: 'ri-search-eye-fill',
      title: 'Inspect',
      desc: 'Our NAPIT registered electrician carries out a thorough inspection and full test of your installation.',
    },
    {
      number: '03',
      icon: 'ri-file-paper-fill',
      title: 'Report',
      desc: 'A detailed written report with all findings, observations and recommendations is issued promptly.',
    },
    {
      number: '04',
      icon: 'ri-tools-fill',
      title: 'Remediate',
      desc: 'Any required remedial works carried out and certified — full compliance achieved in Sleaford.',
    },
  ],

  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=professional%20EICR%20electrical%20inspection%20report%20documents%20certificate%20UK%20property%20formal%20official%20paperwork%20compliance%20Lincolnshire%20Sleaford%20no%20people%20close%20up%20professional%20documentation%20clipboard&width=800&height=600&seq=gal-safe-01-v1&orientation=landscape',
      alt: 'EICR electrical report Sleaford',
      caption: 'EICR Report — Sleaford',
      label: 'EICR Report',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=PAT%20testing%20portable%20appliance%20tester%20connected%20to%20kettle%20UK%20office%20domestic%20property%20professional%20testing%20equipment%20no%20people%20Lincolnshire%20Sleaford%20compliance%20testing%20close%20up%20technical&width=600&height=500&seq=gal-safe-02-v1&orientation=squarish',
      alt: 'PAT testing Sleaford Lincolnshire',
      caption: 'PAT Testing — Lincolnshire',
      label: 'PAT Testing',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=fire%20alarm%20smoke%20detector%20testing%20UK%20commercial%20building%20professional%20electrician%20test%20key%20no%20people%20Lincolnshire%20Sleaford%20BS5839%20compliance%20fire%20safety%20ceiling%20mounted%20detector&width=600&height=500&seq=gal-safe-03-v1&orientation=squarish',
      alt: 'Fire alarm testing Sleaford',
      caption: 'Fire Alarm Testing — Sleaford',
      label: 'Fire Alarm',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=electrical%20installation%20condition%20report%20testing%20consumer%20unit%20UK%20domestic%20property%20professional%20electrician%20testing%20equipment%20clips%20leads%20circuit%20board%20no%20people%20Lincolnshire%20amber%20glow&width=600&height=500&seq=gal-safe-04-v1&orientation=squarish',
      alt: 'Electrical inspection consumer unit Sleaford',
      caption: 'Consumer Unit Inspection — Sleaford',
      label: 'Panel Inspection',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=emergency%20exit%20lighting%20green%20sign%20illuminated%20UK%20commercial%20building%20corridor%20no%20people%20Lincolnshire%20Sleaford%20emergency%20escape%20lighting%20compliance%20test%20BS5266&width=600&height=500&seq=gal-safe-05-v1&orientation=squarish',
      alt: 'Emergency lighting inspection Lincolnshire',
      caption: 'Emergency Lighting — Inspection',
      label: 'Emergency Lighting',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=hard%20wired%20smoke%20alarm%20detector%20ceiling%20UK%20domestic%20property%20white%20no%20people%20Lincolnshire%20Sleaford%20compliance%20installation%20close%20up%20detail%20neat%20professional%20BS5839&width=600&height=500&seq=gal-safe-06-v1&orientation=squarish',
      alt: 'Smoke alarm compliance Sleaford',
      caption: 'Smoke Alarm Compliance — Sleaford',
      label: 'Smoke Alarms',
    },
  ],

  faqHeading: 'Safety & Compliance Questions',
  faqSubheading: 'Common questions about electrical safety inspections and compliance services in Sleaford and across Lincolnshire.',
  faqs: [
    {
      q: 'What is an EICR and do I need one in Sleaford?',
      a: 'An EICR (Electrical Installation Condition Report) is a formal inspection and test of an existing electrical installation — assessing whether it is in a safe and satisfactory condition. EICRs are legally required for all private rented properties in England every 5 years or at each change of tenancy. Homeowners are not legally required to have one, but it is strongly recommended when buying a property or if the installation has not been checked in over 10 years. We carry out EICRs throughout Sleaford and Lincolnshire.',
    },
    {
      q: 'How long does an EICR take in a Sleaford property?',
      a: 'An EICR for a typical 3-bedroom Sleaford house takes 3–4 hours. Larger properties or those with more complex installations take longer. We\'ll need access to all rooms, the consumer unit, and ideally the incoming electricity supply. Power to circuits will need to be switched off periodically during the testing process, so we\'ll arrange a convenient time with you.',
    },
    {
      q: 'What happens if my Sleaford property fails an EICR?',
      a: 'An EICR doesn\'t technically pass or fail — it issues observations coded C1 (danger present, immediate action required), C2 (potentially dangerous, urgent remedial action required), C3 (improvement recommended) or FI (further investigation required). C1 and C2 observations must be rectified before a Satisfactory EICR can be issued. We carry out all required remedial works ourselves — the same team, keeping things simple for Sleaford landlords and homeowners.',
    },
    {
      q: 'How often do landlords need an EICR in Sleaford?',
      a: 'Under the Electrical Safety Standards in the Private Rented Sector (England) Regulations 2020, landlords in Sleaford must have an EICR carried out every 5 years or at each change of tenancy (whichever comes sooner). A copy must be provided to existing tenants within 28 days of the inspection and to new tenants before they move in. Failure to comply can result in fines of up to £30,000.',
    },
    {
      q: 'What is PAT testing and does my Sleaford business need it?',
      a: 'PAT (Portable Appliance Testing) involves the inspection and testing of portable electrical appliances to ensure they are safe to use. There is no specific legal requirement for PAT testing under current UK law, but employers have a duty of care under the Electricity at Work Regulations 1989 to ensure electrical equipment is maintained in a safe condition. We carry out PAT testing for offices, retail units and commercial premises throughout Sleaford and Lincolnshire.',
    },
    {
      q: 'Do I need a fire alarm test certificate for my Sleaford property?',
      a: 'For commercial premises, HMOs and licensed properties in Sleaford, regular fire alarm testing and inspection is a legal requirement under the Regulatory Reform (Fire Safety) Order 2005. A BS 5839 inspection certificate must be in place and available for inspection. For domestic properties, smoke alarms must be installed on every floor and tested regularly. We carry out fire alarm testing and issue the required documentation for Sleaford commercial premises.',
    },
    {
      q: 'Can you carry out an electrical survey before I buy a property in Sleaford?',
      a: 'Yes — a pre-purchase electrical survey is one of the most valuable services we offer in Sleaford. It gives you a full picture of the electrical installation\'s condition before exchange of contracts — identifying potential remedial costs and any safety concerns. We provide a clear, easy-to-read written report that you can use in purchase negotiations or to plan post-purchase renovation budgets.',
    },
    {
      q: 'Do you carry out the remedial works after an EICR in Sleaford?',
      a: 'Yes — this is one of our key differentiators. Many inspection companies issue the EICR report and leave you to find another contractor for the remedials. We carry out all EICR-identified remedial works ourselves — the same NAPIT registered team, familiar with your installation, completing everything to the same high standard. You receive a Satisfactory EICR once all remedials are complete.',
    },
    {
      q: 'How much does an EICR cost in Sleaford?',
      a: 'EICR pricing in Sleaford depends on the property size and the number of circuits. A 1–2 bedroom property starts from around £130–£180. A 3-bedroom property is typically £150–£220. Larger properties are priced on request. We provide a fixed written quote before attending — call 01529 265060 for an estimate for your specific Sleaford property.',
    },
  ],

  mapHeading: 'Safety & Compliance Across Sleaford & Lincolnshire',
  mapSubheading: 'EICR reports, PAT testing and electrical compliance for homes and businesses throughout Sleaford and Lincolnshire.',
  mapEmbedSrc: 'https://maps.google.com/maps?q=Sleaford,Lincolnshire,UK&t=&z=12&ie=UTF8&iwloc=&output=embed',
  mapAreaList: ['Sleaford', 'Grantham', 'Boston', 'Spalding', 'Lincoln', 'Newark-on-Trent', 'Stamford', 'Bourne', 'Heckington', 'Ruskington', 'Ancaster', 'Cranwell', 'Leadenham', 'Navenby', 'Metheringham', 'Kirkby la Thorpe'],

  formUrl: 'https://readdy.ai/api/form/d73s2osprit363gl0dg0',
};

export default function SafetyCompliancePage() {
  return <ServiceTemplate config={config} />;
}
